import React, { useState, useEffect, useContext } from 'react';
import {
  Table,
  TableHead,
  TableBody,
  TableCell,
  TableRow,
  TableSortLabel,
  TableContainer,
  TablePagination,
  Paper,
  Typography,
  Tooltip,
  Box,
  LinearProgress,
  Checkbox,
  IconButton,
  CircularProgress,
  InputAdornment,
} from '@mui/material';
import * as XLSX from 'xlsx';
import { green, red } from '../types';
import { FaRegFileExcel } from 'react-icons/fa';
import { AppContext } from '../../AppContext';
import axios from 'axios';
import TextField from '@mui/material/TextField';

interface Customer {
  id: string;
  name: string;
  accountNumber?: string | null;
  numActiveDevices: number;
  collisionRisk: number | null;
  averageCollisionRisk?: number | null;
  riskRelativeToHanover: number | null;
  engagementScore: number;
  safetyScore: number;
  generalScore: number;
  reportingScore: number;
  maintenanceScore: number;
  productivityScore: number;
  additionalSolutionsScore: number;
  active: boolean;
  collisionRiskImprovement?: number | null;
  riskTrend?: number | null;
}

export interface FleetData {
  id: string;
  accountNumber?: string | null;
  fleetName: string;
  numActiveDevices: number;
  collisionRisk: number | null;
  benchmarkRisk: number;
  riskRelativeToHanover: number | null;
  engagementScore: number;
  safetyScore: number;
  generalScore: number;
  reportingScore: number;
  maintenanceScore: number;
  productivityScore: number;
  additionalSolutionsScore: number;
  active: boolean;
  collisionRiskImprovement?: number | null;
  riskTrend?: number | null;
}

interface CollisionRiskTableProps {
  selectedFleetId: string | null;
  onFleetSelect: (fleetId: string) => void;
  selectedCustomerIds: string[];
  onCheckboxChange: (customerId: string) => void;
  maxSelectedCustomers: number;
  selectedCustomerCount: number;
  availableCustomerIds: string[];
  showInactive: boolean;
  startDate: Date | null;
  endDate: Date | null;
}

type Order = 'asc' | 'desc';
type SortableKeys =
  | 'fleetName'
  | 'collisionRisk'
  | 'riskRelativeToHanover'
  | 'engagementScore'
  | 'collisionRiskImprovement'
  | 'riskTrend';

const CollisionRiskTable: React.FC<CollisionRiskTableProps> = ({
  selectedFleetId,
  onFleetSelect,
  selectedCustomerIds,
  onCheckboxChange,
  maxSelectedCustomers,
  selectedCustomerCount,
  availableCustomerIds,
  showInactive,
  startDate,
  endDate,
}) => {
  const [fleetData, setFleetData] = useState<FleetData[]>([]);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [isLoading, setIsLoading] = useState(false); // Excel download loading
  const [tableLoading, setTableLoading] = useState(true); // table data loading

  const { user: appUser } = useContext(AppContext);

  const [sortBy, setSortBy] = useState<SortableKeys>('fleetName');
  const [sortOrder, setSortOrder] = useState<Order>('asc');

  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  useEffect(() => {
    const handle = setTimeout(() => {
      setDebouncedSearch(searchTerm.trim());
      setPage(1);
    }, 300);

    return () => clearTimeout(handle);
  }, [searchTerm]);

  const prepareFleetData = (customers: Customer[]): FleetData[] =>
    customers.map((c) => ({
      id: c.id,
      fleetName: c.name,
      accountNumber: c.accountNumber ?? null,
      numActiveDevices: c.numActiveDevices,
      collisionRisk: c.collisionRisk ?? null,
      benchmarkRisk: c.averageCollisionRisk ?? 0,
      riskRelativeToHanover: c.riskRelativeToHanover ?? null,
      engagementScore: c.engagementScore,
      safetyScore: c.safetyScore,
      generalScore: c.generalScore,
      reportingScore: c.reportingScore,
      maintenanceScore: c.maintenanceScore,
      productivityScore: c.productivityScore,
      additionalSolutionsScore: c.additionalSolutionsScore,
      active: c.active,
      collisionRiskImprovement: c.collisionRiskImprovement ?? null,
      riskTrend: c.riskTrend,
    }));

  useEffect(() => {
    if (!appUser?.region_id || !startDate || !endDate) return;

    const fetchMostRecentCollisionRisks = async () => {
      try {
        setTableLoading(true);
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/customers/most_recent_collision_risks`,
          {
            params: {
              page,
              page_size: pageSize,
              sort_by: sortBy,
              sort_order: sortOrder,
              region_id: appUser.region_id,
              start_date: startDate,
              end_date: endDate,
              active: (!showInactive).toString(),
              search: debouncedSearch || undefined,
            },
          }
        );
        const fetched: Customer[] = response.data.data.map((c: any) => ({
          ...c,
          id: c.id.toString(),
          collisionRiskImprovement: c.collisionRiskImprovement,
          riskTrend: c.riskTrend,
        }));
        setFleetData(prepareFleetData(fetched));
        setTotalCount(response.data.total_count);
      } catch (error) {
        console.error(error);
      } finally {
        setTableLoading(false);
      }
    };
    fetchMostRecentCollisionRisks();
  }, [
    page,
    pageSize,
    sortBy,
    sortOrder,
    appUser?.region_id,
    showInactive,
    startDate,
    endDate,
    debouncedSearch,
  ]);

  useEffect(() => {
    setPage(1);
  }, [showInactive]);

  const handleRequestSort = (property: SortableKeys) => {
    const isAsc = sortBy === property && sortOrder === 'asc';
    setSortOrder(isAsc ? 'desc' : 'asc');
    setSortBy(property);
    setPage(1);
  };

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage + 1);
  };

  const handleChangeRowsPerPage = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPageSize(parseInt(e.target.value, 10));
    setPage(1);
  };

  const getColorByComparison = (value: number | null, benchmark: number) =>
    value == null ? 'inherit' : value < benchmark ? green : red;

  const handleDownloadExcel = async () => {
    try {
      setIsLoading(true);
      const regionId = appUser?.region_id ?? '1';

      const params: Record<string, any> = {
        region_id: regionId,
        all: true,
        sort_by: sortBy,
        sort_order: sortOrder,
        active: (!showInactive).toString(),
      };

      if (startDate) {
        params.start_date = startDate.toISOString().slice(0, 10);
      }
      if (endDate) {
        params.end_date = endDate.toISOString().slice(0, 10);
      }

      if (selectedCustomerIds.length) {
        params.selected_ids = selectedCustomerIds.join(',');
      }

      const resp = await axios.get(
        `${process.env.REACT_APP_API_URL}/customers/most_recent_collision_risks`,
        { params }
      );

      const rows = resp.data.data.map((r: any) => {
        const trend = r.riskTrend as number | null;
        const isGood = trend != null && trend > 0;
        const sign = trend == null ? '' : isGood ? '-' : '+';
        const abs = trend == null ? '' : Math.abs(trend).toFixed(2);

        return {
          'Fleet Name': r.name,
          'Account Number': r.accountNumber || '',
          'Active Devices': r.numActiveDevices,
          'Collision Risk': r.collisionRisk,
          'Collision Risk Improvement':
            r.collisionRiskImprovement != null
              ? `${r.collisionRiskImprovement.toFixed(2)}%`
              : '',
          'Risk Trend': r.riskTrend != null ? `${sign}${abs}%` : '',
          'Risk Relative': r.riskRelativeToHanover,
          'Engagement Score': r.engagementScore,
        };
      });

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(
        wb,
        XLSX.utils.json_to_sheet(rows),
        'Collision Risks'
      );
      XLSX.writeFile(wb, 'collision_risk_data.xlsx');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Typography
        variant="h6"
        sx={{
          p: 2,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        Collision Risk Table

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <TextField
            size="small"
            label="Search"
            variant="outlined"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Name or account number..."
            sx={{ minWidth: 230 }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  {tableLoading && searchTerm.trim().length > 0 && (
                    <CircularProgress size={18} />
                  )}
                </InputAdornment>
              ),
            }}
          />
          <Tooltip title="Download Excel">
            <span>
              <IconButton onClick={handleDownloadExcel} disabled={isLoading}>
                {isLoading ? (
                  <CircularProgress size={24} />
                ) : (
                  <FaRegFileExcel size={24} color="#4CAF50" />
                )}
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Typography>

      {tableLoading && fleetData.length === 0 ? (
        <Box
          sx={{
            mb: 4,
            height: 300,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: 1,
            border: '1px solid rgba(0,0,0,0.12)',
          }}
        >
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer
          component={Paper}
          sx={{ mb: 4, maxHeight: '830px', overflowY: 'auto' }}
        >
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Typography variant="caption">
                    Select (max {maxSelectedCustomers})
                  </Typography>
                </TableCell>
                <TableCell
                  sortDirection={sortBy === 'fleetName' ? sortOrder : false}
                >
                  <TableSortLabel
                    active={sortBy === 'fleetName'}
                    direction={
                      sortBy === 'fleetName' ? sortOrder : 'asc'
                    }
                    onClick={() => handleRequestSort('fleetName')}
                  >
                    Fleet
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <Typography variant="subtitle2">
                    Active Devices
                  </Typography>
                </TableCell>
                <TableCell
                  sortDirection={
                    sortBy === 'collisionRisk' ? sortOrder : false
                  }
                >
                  <Tooltip
                    title="Collision risk for the next 100,000 miles"
                    arrow
                  >
                    <TableSortLabel
                      active={sortBy === 'collisionRisk'}
                      direction={
                        sortBy === 'collisionRisk' ? sortOrder : 'asc'
                      }
                      onClick={() =>
                        handleRequestSort('collisionRisk')
                      }
                    >
                      Collision Risk
                    </TableSortLabel>
                  </Tooltip>
                </TableCell>
                <TableCell
                  sortDirection={
                    sortBy === 'collisionRiskImprovement'
                      ? sortOrder
                      : false
                  }
                >
                  <Tooltip
                    title={
                      <>
                        <Typography variant="body2">
                          Collision risk improvement for current date
                          range.
                        </Typography>
                        <Typography variant="body2">
                          Negative (green) → risk is decreasing;
                          Positive (red) → risk is increasing.
                        </Typography>
                      </>
                    }
                    arrow
                  >
                    <TableSortLabel
                      active={sortBy === 'collisionRiskImprovement'}
                      direction={
                        sortBy === 'collisionRiskImprovement'
                          ? sortOrder
                          : 'asc'
                      }
                      onClick={() =>
                        handleRequestSort(
                          'collisionRiskImprovement'
                        )
                      }
                    >
                      Collision Risk Improvement
                    </TableSortLabel>
                  </Tooltip>
                </TableCell>
                <TableCell
                  sortDirection={
                    sortBy === 'riskTrend' ? sortOrder : false
                  }
                >
                  <Tooltip
                    title={
                      <>
                        <Typography variant="body2">
                          Linear-trend projection of collision risk for
                          current date range.
                        </Typography>
                        <Typography variant="body2">
                          Negative (green) → risk is decreasing;
                          Positive (red) → risk is increasing.
                        </Typography>
                      </>
                    }
                    arrow
                  >
                    <TableSortLabel
                      active={sortBy === 'riskTrend'}
                      direction={
                        sortBy === 'riskTrend' ? sortOrder : 'asc'
                      }
                      onClick={() => handleRequestSort('riskTrend')}
                    >
                      Risk Trend
                    </TableSortLabel>
                  </Tooltip>
                </TableCell>
                <TableCell
                  sortDirection={
                    sortBy === 'riskRelativeToHanover'
                      ? sortOrder
                      : false
                  }
                >
                  <TableSortLabel
                    active={sortBy === 'riskRelativeToHanover'}
                    direction={
                      sortBy === 'riskRelativeToHanover'
                        ? sortOrder
                        : 'asc'
                    }
                    onClick={() =>
                      handleRequestSort('riskRelativeToHanover')
                    }
                  >
                    Risk Relative to Other Fleets
                  </TableSortLabel>
                </TableCell>
                <TableCell
                  sortDirection={
                    sortBy === 'engagementScore' ? sortOrder : false
                  }
                >
                  <TableSortLabel
                    active={sortBy === 'engagementScore'}
                    direction={
                      sortBy === 'engagementScore'
                        ? sortOrder
                        : 'asc'
                    }
                    onClick={() =>
                      handleRequestSort('engagementScore')
                    }
                  >
                    Engagement Score
                  </TableSortLabel>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {fleetData.map((fleet) => (
                <TableRow
                  key={fleet.id}
                  hover
                  selected={fleet.id === selectedFleetId}
                  sx={{ cursor: 'pointer' }}
                  onClick={() => onFleetSelect(fleet.id)}
                >
                  <TableCell padding="checkbox">
                    <Tooltip
                      title={
                        !availableCustomerIds.includes(fleet.id)
                          ? 'No data available for this fleet'
                          : !selectedCustomerIds.includes(fleet.id) &&
                            selectedCustomerCount >=
                              maxSelectedCustomers
                          ? `You can only select ${maxSelectedCustomers} fleets at a time`
                          : ''
                      }
                      arrow
                      placement="top"
                    >
                      <span>
                        <Checkbox
                          checked={selectedCustomerIds.includes(
                            fleet.id
                          )}
                          onChange={() =>
                            onCheckboxChange(fleet.id)
                          }
                          disabled={
                            (!selectedCustomerIds.includes(
                              fleet.id
                            ) &&
                              selectedCustomerCount >=
                                maxSelectedCustomers) ||
                            !availableCustomerIds.includes(
                              fleet.id
                            )
                          }
                          inputProps={{
                            'aria-label': `Select ${fleet.fleetName}`,
                          }}
                        />
                      </span>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                      }}
                    >
                      <Typography variant="body2">
                        {fleet.fleetName}
                      </Typography>
                      {fleet.accountNumber && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ mt: 0.25 }}
                        >
                          {fleet.accountNumber}
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {fleet.numActiveDevices ?? 'N/A'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" component="span">
                      {fleet.collisionRisk != null
                        ? `${fleet.collisionRisk.toFixed(2)}%`
                        : 'No Data'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {fleet.collisionRiskImprovement != null ? (
                      <Tooltip
                        title="Calculated by comparing the first and last data point"
                        arrow
                        placement="top"
                      >
                        <Typography
                          variant="body2"
                          component="span"
                          sx={{
                            color:
                              fleet.collisionRiskImprovement < 0
                                ? green
                                : red,
                            cursor: 'help',
                          }}
                        >
                          {`${
                            fleet.collisionRiskImprovement < 0
                              ? ''
                              : '+'
                          }${fleet.collisionRiskImprovement.toFixed(
                            2
                          )}%`}
                        </Typography>
                      </Tooltip>
                    ) : (
                      <Typography variant="body2">
                        No Data
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {fleet.riskTrend != null ? (
                      (() => {
                        const trend = fleet.riskTrend;
                        const isGood = trend > 0;
                        const color = isGood ? green : red;
                        const sign = isGood ? '-' : '+';
                        const abs = Math.abs(trend).toFixed(2);
                        return (
                          <Tooltip
                            title="Linear-regression trend between start and end dates"
                            arrow
                          >
                            <Typography
                              variant="body2"
                              component="span"
                              sx={{ color, cursor: 'help' }}
                            >
                              {`${sign}${abs}%`}
                            </Typography>
                          </Tooltip>
                        );
                      })()
                    ) : (
                      <Typography variant="body2">
                        No Data
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {fleet.riskRelativeToHanover != null ? (
                      <Tooltip
                        title={
                          fleet.benchmarkRisk != null
                            ? `Compared to average risk: ${fleet.benchmarkRisk.toFixed(
                                2
                              )}%`
                            : 'No benchmark data'
                        }
                        arrow
                        placement="top"
                      >
                        <Typography
                          variant="body2"
                          component="span"
                          sx={{
                            color:
                              fleet.collisionRisk != null
                                ? getColorByComparison(
                                    fleet.collisionRisk,
                                    fleet.benchmarkRisk
                                  )
                                : 'inherit',
                            cursor: 'help',
                          }}
                        >
                          {`${
                            fleet.riskRelativeToHanover >= 0
                              ? '+'
                              : ''
                          }${fleet.riskRelativeToHanover.toFixed(
                            2
                          )}%`}
                        </Typography>
                      </Tooltip>
                    ) : (
                      <Typography variant="body2">
                        No Data
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Tooltip
                      title={
                        <>
                          <Typography variant="body2">
                            Safety:{' '}
                            {Math.round(
                              fleet.safetyScore
                            )}
                            %
                          </Typography>
                          <Typography variant="body2">
                            General:{' '}
                            {Math.round(
                              fleet.generalScore
                            )}
                            %
                          </Typography>
                          <Typography variant="body2">
                            Reporting:{' '}
                            {Math.round(
                              fleet.reportingScore
                            )}
                            %
                          </Typography>
                          <Typography variant="body2">
                            Maintenance:{' '}
                            {Math.round(
                              fleet.maintenanceScore
                            )}
                            %
                          </Typography>
                          <Typography variant="body2">
                            Productivity:{' '}
                            {Math.round(
                              fleet.productivityScore
                            )}
                            %
                          </Typography>
                          <Typography variant="body2">
                            Additional Solutions:{' '}
                            {Math.round(
                              fleet.additionalSolutionsScore
                            )}
                            %
                          </Typography>
                        </>
                      }
                      arrow
                      placement="top"
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                        }}
                      >
                        <Box
                          sx={{ width: '100%', mr: 1 }}
                        >
                          <LinearProgress
                            variant="determinate"
                            value={fleet.engagementScore}
                          />
                        </Box>
                        <Box sx={{ minWidth: 35 }}>
                          <Typography
                            variant="body2"
                            color="textSecondary"
                          >
                            {`${Math.round(
                              fleet.engagementScore
                            )}%`}
                          </Typography>
                        </Box>
                      </Box>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          <TablePagination
            component="div"
            count={totalCount}
            page={page - 1}
            onPageChange={handleChangePage}
            rowsPerPage={pageSize}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[10, 25, 50]}
          />
        </TableContainer>
      )}
    </>
  );
};

export default CollisionRiskTable;
